import { Tab, Tabs, TabsProps } from '@heroui/react';
import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import SearchableTabs from './SearchableTabs';
import { useAppDispatch, useAppSelector } from '../../../core/rootHooks';
import { setTab, setSubtab } from '../../../routing/pageContextSlice';
import { cleanLabelForKey } from '../../../utils/helpers';
import { useNavigationType, useParams } from 'react-router-dom';
import { RouteParams } from '../../../routing/routingTypes';

interface IDynamicTabProps<T, P extends {} = {}> {
	/**
	 * An array of static tab items to render.
	 * Each item should have a unique `id`, a `label` for the tab title, and `content` for the tab's content.
	 */
	items?: { id: string; label: string; content: ReactNode }[];

	/**
	 * A React functional component used to render the content of dynamic tabs.
	 * This component will be used in conjunction with `dynamicItemData` and `dynamicItemProps`.
	 */
	dynamicItem?: React.FC<P>;

	/**
	 * A function that takes an item from `dynamicItemData` and returns props to be passed to the `dynamicItem` component.
	 * This allows customization of the props for each dynamic tab.
	 */
	dynamicItemProps?: (item: T) => P;

	/**
	 * An array of data items used to generate dynamic tabs.
	 * Each item in this array will correspond to a dynamic tab rendered using the `dynamicItem` component.
	 */
	dynamicItemData?: T[];

	/**
	 * An array of React nodes to render as static tabs.
	 * These tabs are rendered alongside the dynamic tabs, if any.
	 */
	staticItems?: ReactNode[];

	/**
	 * The background color of the tabs.
	 * Defaults to `'msi-chart-midblue'` if not provided.
	 */
	tabColor?: string;

	/**
	 * Additional props to pass to the `Tabs` component from the `@heroui/react` library.
	 * This allows customization of the underlying `Tabs` component.
	 */
	props?: TabsProps;

	/**
	 * A boolean that determines whether to add extra spacing to the component.
	 * Defaults to `true`.
	 */
	giveSpace?: boolean;

	/**
	 * A string representing a CSS class to apply padding to the component.
	 * Defaults to `'pl-36'` if not provided.
	 */
	paddingClass?: string;

	/**
	 * A boolean indicating if this is a subtab component.
	 * When true, uses pageContext.subtab state instead of pageContext.tab.
	 * Defaults to `false`.
	 */
	isSubtab?: boolean;

	/**
	 * A boolean indicating if this component should operate in local mode.
	 * When true, tab changes don't affect Redux state or routing.
	 * Useful for nested tabs that should only manage internal UI state.
	 * Defaults to `false`.
	 */
	isLocalMode?: boolean;
}

const DynamicTab = <T, P extends {} = {}>({
	items,
	dynamicItem: DynamicItemComponent,
	dynamicItemProps,
	dynamicItemData,
	staticItems,
	tabColor = '',
	props,
	giveSpace = true,
	paddingClass = 'pl-36',
	isSubtab = false,
	isLocalMode = false,
}: IDynamicTabProps<T, P>) => {
	const dispatch = useAppDispatch();
	const pageContext = useAppSelector((state) => state.pageContextSlice);
	const isDynamicMode = !!dynamicItemData;

	const { feature, subtab } = useParams<RouteParams>();

	// Local state for when component operates independently of routing
	const [localSelectedKey, setLocalSelectedKey] = useState<string>(() => {
		if (isLocalMode) {
			if (isDynamicMode) {
				return staticItems ? 'fleet' : 'vessel';
			} else {
				return cleanLabelForKey(items?.[0]?.label || '');
			}
		}
		return '';
	});

	//Handle tab selection - dispatch to Redux instead of using HeroUI's built-in state
	const handleTabChange = (key: React.Key) => {
		const tabKey = cleanLabelForKey(key.toString());

		// If in local mode, just update local state
		if (isLocalMode) {
			setLocalSelectedKey(tabKey);
			return;
		}

		// Otherwise, handle Redux state as before
		if (isSubtab) {
			dispatch(setSubtab(tabKey));
		} else {
			dispatch(setTab(tabKey));
			// Clear subtab when switching main tabs
			dispatch(setSubtab(undefined));
		}
	};

	const lastLocationRef = useRef<string | null>(null);
	const isRealPopRef = useRef(false);
	const navigationType = useNavigationType();

	useLayoutEffect(() => {
		if (navigationType === 'POP' && lastLocationRef.current && lastLocationRef.current !== location.pathname) {
			isRealPopRef.current = true;
		} else {
			isRealPopRef.current = false;
		}

		lastLocationRef.current = location.pathname;
	});

	// Set initial tab on component mount
	useEffect(() => {
		// Skip if in local mode - local state is already initialized
		if (isLocalMode || !pageContext.feature) return;

		// Get the default selected key
		const initialKey = (() => {
			if (isSubtab) {
				return pageContext.subtab && isRealPopRef.current ? subtab : (pageContext.subtab || cleanLabelForKey(items?.[0]?.label || ''));
			}

			if (isDynamicMode) {
				return staticItems ? 'fleet' : 'vessel';
			}

			return cleanLabelForKey(items?.[0]?.label || '') || pageContext.tab;
		})();

		// Only dispatch if we don't have the current state set
		if (isSubtab && initialKey && (!pageContext.subtab || pageContext.subtab !== subtab)) {
			dispatch(setSubtab(initialKey));
		} else if (
			!isSubtab &&
			initialKey &&
			((pageContext.feature !== feature && pageContext.tab) || (pageContext.feature === feature && !pageContext.tab))
		) {
			dispatch(setTab(initialKey));
		}
	}, [
		dispatch,
		isLocalMode,
		isSubtab,
		isDynamicMode,
		staticItems,
		items,
		pageContext.feature,
		pageContext.tab,
		pageContext.subtab,
		subtab,
	]);

	const selectedKey = useMemo(() => {
		// If in local mode, use local state
		if (isLocalMode) {
			return localSelectedKey;
		}

		if (isSubtab) {
			let subTabKey = cleanLabelForKey(items?.[0]?.label || '');

			if (pageContext.subtab) {
				subTabKey = pageContext.subtab;
			}
			return subTabKey;
		}

		let tabKey = cleanLabelForKey(items?.[0]?.label || '');
		if (pageContext.tab) {
			tabKey = pageContext.tab;
		}

		if (isDynamicMode) {
			return tabKey || (staticItems ? 'fleet' : 'vessel');
		} else {
			return tabKey;
		}
	}, [isLocalMode, localSelectedKey, isSubtab, pageContext.subtab, pageContext.tab, items, isDynamicMode, staticItems]);

	return (
		<div className="flex flex-col w-full h-full">
			<Tabs
				size="sm"
				selectedKey={selectedKey}
				defaultSelectedKey={0}
				onSelectionChange={handleTabChange}
				classNames={{
					tabList: `flex gap-1 rounded-none bg-msi-white-color pb-0 ${giveSpace && ((props?.isVertical && 'pt-12 pr-6') || paddingClass)}`,
					tab: `rounded-none border-none data-[selected=true]:opacity-100 opacity-60 bg-${tabColor || 'msi-chart-midblue'}`,
					tabContent: `!text-msi-white-color`,
					cursor: `rounded-none bg-${tabColor || 'msi-white-color'}`,
					panel: 'flex flex-auto w-full overflow-y-auto',
					base: `${props?.isVertical ? 'h-full' : 'w-full'} ${props?.isVertical ? ' border-r' : ' border-b'} border-msi-darkgrey-color-300 ${(props?.isVertical && 'min-w-32  justify-center') || ''} `,
				}}
				{...props}
			>
				{isDynamicMode ? (
					<>
						{staticItems?.map((item) => (
							<Tab key={'fleet'} title={'By Fleet'}>
								{item}
							</Tab>
						))}

						<Tab key={'vessel'} title={'By Vessel'}>
							<SearchableTabs
								dynamicItemData={dynamicItemData}
								dynamicItemProps={dynamicItemProps}
								dynamicItem={DynamicItemComponent}
								tabColor={tabColor}
							/>
						</Tab>
					</>
				) : (
					items?.map((item) => (
						<Tab key={cleanLabelForKey(item.label)} title={item.label}>
							{item.content}
						</Tab>
					))
				)}
			</Tabs>
		</div>
	);
};

export default DynamicTab;
